'use strict';

/**
 * NotesPage -- the full-page view of the caller's private markdown notes, rendered into the main
 * workspace (#Plansheet-Workspace) rather than the notebook rail. It is the page-sized twin of the
 * Notebook's Notes tab: same list/select/save/delete flow through providers.NotebookAPI, the same
 * real pict-section-markdowneditor child, just laid out two-column (note list left, editor right) on
 * a wide screen and stacked on a phone.
 *
 * Its render/view state lives under AppData.NotesPage (list + selection + draft title) and the
 * editor buffer under AppData.NotesPageEditor.Segments, kept separate from the rail notebook's
 * AppData.Notebook / AppData.NotebookNoteEditor so the two never fight over a buffer. The child
 * editor (Plansheet-NotesPage-Editor) owns #PS-NotesPage-MDE; because each panel render recreates
 * that host div, the editor is re-mounted after every render (destroy -> render -> marshalToView),
 * with an across-repaint capture guard in onBeforeRender so in-progress edits survive.
 */

const libPictView = require('pict-view');

class PictViewNotesPage extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		// The editor reads/writes its segments here (ContentDataAddress on the editor instance).
		this.pict.AppData.NotesPageEditor = this.pict.AppData.NotesPageEditor || { Segments: [] };
		// Which note the live editor is currently showing -- gates the across-repaint edit capture.
		this._editorMountedNoteID = 0;
	}

	_page() { return this.pict.AppData.NotesPage || (this.pict.AppData.NotesPage = { Notes: [] }); }
	_notebook() { return this.pict.AppData.Notebook || {}; }
	_api() { return this.pict.providers.NotebookAPI; }
	_noteEditor() { return this.pict.views['Plansheet-NotesPage-Editor']; }

	// Load (or refresh) the caller's notebook data, mirror the Notes into this page's namespace, then
	// repaint. NotebookAPI.loadNotebook is the one read that backs both the rail and this page.
	show()
	{
		let tmpAPI = this._api();
		if (!tmpAPI || typeof tmpAPI.loadNotebook !== 'function') { this.render(); return; }
		return tmpAPI.loadNotebook().then(() =>
		{
			this._page().Notes = this._notebook().Notes || [];
			this.render();
		}).catch(() => this.render());
	}

	onBeforeRender(pRenderable)
	{
		let tmpD = this._page();
		let tmpSelectedID = tmpD.SelectedNoteID || 0;

		// Preserve in-progress edits across this repaint: if the live editor is showing the selected
		// note, pull its current markdown into the segment buffer so the remount restores it. Guarded
		// on _editorMountedNoteID so a note SWITCH (which seeds the new note's segments) is not clobbered.
		if (tmpSelectedID && this._editorMountedNoteID === tmpSelectedID)
		{
			let tmpEd = this._noteEditor();
			if (tmpEd && tmpEd.initialRenderComplete && typeof tmpEd.getAllContent === 'function')
			{
				try { this.pict.AppData.NotesPageEditor.Segments = [{ Content: tmpEd.getAllContent('\n\n') }]; } catch (pErr) { /* ignore */ }
			}
		}

		let tmpNotes = tmpD.Notes || [];
		tmpD.NoteRows = tmpNotes.map((pN) => ({ IDNote: pN.IDNote, Title: pN.Title || 'Untitled note', Active: pN.IDNote === tmpSelectedID }));
		tmpD.NotesEmpty = tmpNotes.length ? [] : [1];
		let tmpSelected = tmpNotes.find((pN) => pN.IDNote === tmpSelectedID);
		// The editor owns the body content (AppData.NotesPageEditor.Segments); the slot only carries
		// the id + the title field value.
		tmpD.EditorSlot = tmpSelected
			? [{
				IDNote: tmpSelected.IDNote,
				Title: (tmpD.DraftTitle != null) ? tmpD.DraftTitle : (tmpSelected.Title || '')
			}]
			: [];
		// One-or-zero-element slot: the empty editor pane hint when nothing is selected.
		tmpD.NoSelectionSlot = tmpSelected ? [] : [1];

		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		if (this._page().SelectedNoteID) { this._mountNoteEditor(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	// The panel render recreates the editor's host div, so (re)mount the editor into the fresh host.
	_mountNoteEditor()
	{
		let tmpEd = this._noteEditor();
		if (!tmpEd)
		{
			this.pict.ContentAssignment.assignContent('#PS-NotesPage-MDE',
				'<div class="ps-notespage-hint">The markdown editor is not available in this build.</div>');
			return;
		}
		try { if (tmpEd.initialRenderComplete) { tmpEd.destroy(); tmpEd.initialRenderComplete = false; } } catch (pErr) { /* ignore */ }
		tmpEd.render();
		tmpEd.marshalToView();
		this._editorMountedNoteID = this._page().SelectedNoteID || 0;
	}

	// Selecting a note is a NAVIGATION to /note/:id (clicking the open note again deselects -> /notes), so
	// the open note deep-links and survives a reload (the selection used to live only in memory). The route
	// calls showNote to actually select + render.
	selectNote(pID)
	{
		let tmpApp = this.pict.PictApplication;
		let tmpAlready = (this._page().SelectedNoteID === pID);
		if (tmpApp && typeof tmpApp.showNote === 'function' && typeof tmpApp.showNotes === 'function')
		{
			if (tmpAlready) { tmpApp.showNotes(); } else { tmpApp.showNote(pID); }
			return;
		}
		// Fallback (no app/router): the original in-place select.
		let tmpD = this._page();
		tmpD.SelectedNoteID = tmpAlready ? 0 : pID;
		let tmpNote = (tmpD.Notes || []).find((pN) => pN.IDNote === tmpD.SelectedNoteID);
		tmpD.DraftTitle = tmpNote ? (tmpNote.Title || '') : null;
		this.pict.AppData.NotesPageEditor.Segments = tmpNote ? [{ Content: tmpNote.Content || '' }] : [];
		this.render();
	}

	// Route entry for /note/:id: select the note into the editor pane and repaint. Seeds the editor body,
	// loads the notebook first on a cold deep-link so the note resolves, and titles the page after it.
	showNote(pID)
	{
		let tmpID = Number(pID) || 0;
		let tmpSelf = this;
		let fApply = () =>
		{
			let tmpD = tmpSelf._page();
			tmpD.SelectedNoteID = tmpID;
			let tmpNote = (tmpD.Notes || []).find((pN) => pN.IDNote === tmpID);
			tmpD.DraftTitle = tmpNote ? (tmpNote.Title || '') : null;
			if (!tmpSelf.pict.AppData.NotesPageEditor) { tmpSelf.pict.AppData.NotesPageEditor = { Segments: [] }; }
			tmpSelf.pict.AppData.NotesPageEditor.Segments = tmpNote ? [{ Content: tmpNote.Content || '' }] : [];
			tmpSelf.render();
			let tmpApp = tmpSelf.pict.PictApplication;
			if (tmpApp && typeof tmpApp._setPageTitle === 'function') { tmpApp._setPageTitle((tmpNote && tmpNote.Title) || 'Note'); }
		};
		let tmpAPI = this._api();
		if (tmpAPI && typeof tmpAPI.loadNotebook === 'function' && !(this._page().Notes || []).length)
		{
			tmpAPI.loadNotebook().then(() => { tmpSelf._page().Notes = tmpSelf._notebook().Notes || []; fApply(); }).catch(fApply);
		}
		else { fApply(); }
	}

	// Route entry for /notes: clear any selection (the list, nothing open) and load + render.
	showList()
	{
		let tmpD = this._page();
		tmpD.SelectedNoteID = 0;
		tmpD.DraftTitle = null;
		if (this.pict.AppData.NotesPageEditor) { this.pict.AppData.NotesPageEditor.Segments = []; }
		this._editorMountedNoteID = 0;
		return this.show();
	}

	// The title is a plain input marshalled into the draft on the way in (no re-render per keystroke);
	// the body lives in the markdown editor and is read on save.
	onDraftTitle(pValue) { this._page().DraftTitle = pValue; }

	// Images pasted / dragged / picked in a note are owned by that note (OwnerType 'Note', IDOwner =
	// the note's id) so the bytes live in Parime instead of bloating the note as a base64 data URI.
	// The note always has a real id by the time its editor is open (newNote creates server-side first).
	noteImageOwnerContext() { return { OwnerType: 'Note', IDOwner: this._page().SelectedNoteID || 0 }; }

	newNote()
	{
		let tmpAPI = this._api();
		if (!tmpAPI) { return; }
		let tmpSelf = this;
		tmpAPI.createNote({ Title: 'Untitled note', Content: '' }).then((pRec) =>
		{
			if (!pRec || !pRec.IDNote) { return tmpSelf.show(); }
			// Refresh the notebook so the new note is in the list, then open it via its route (/note/:id).
			let fGo = () =>
			{
				let tmpApp = tmpSelf.pict.PictApplication;
				if (tmpApp && typeof tmpApp.showNote === 'function') { tmpApp.showNote(pRec.IDNote); } else { tmpSelf.show(); }
			};
			if (typeof tmpAPI.loadNotebook === 'function') { tmpAPI.loadNotebook().then(() => { tmpSelf._page().Notes = tmpSelf._notebook().Notes || []; fGo(); }).catch(fGo); }
			else { fGo(); }
		}).catch(() => {});
	}

	saveNote()
	{
		let tmpAPI = this._api();
		let tmpD = this._page();
		if (!tmpAPI || !tmpD.SelectedNoteID) { return; }
		let tmpEd = this._noteEditor();
		let tmpContent = (tmpEd && typeof tmpEd.getAllContent === 'function') ? tmpEd.getAllContent('\n\n') : '';
		tmpAPI.updateNote(tmpD.SelectedNoteID, { Title: tmpD.DraftTitle || '', Content: tmpContent })
			.then(() => this.show()).catch(() => {});
	}

	deleteNote(pID)
	{
		let tmpAPI = this._api();
		if (!tmpAPI) { return; }
		let tmpSelf = this;
		tmpAPI.deleteNote(pID).then(() =>
		{
			let tmpD = tmpSelf._page();
			if (tmpD.SelectedNoteID === pID)
			{
				tmpD.SelectedNoteID = 0;
				tmpD.DraftTitle = null;
				if (tmpSelf.pict.AppData.NotesPageEditor) { tmpSelf.pict.AppData.NotesPageEditor.Segments = []; }
				tmpSelf._editorMountedNoteID = 0;
				// Deleting the open note leaves the list route (so the URL no longer points at a gone note).
				let tmpApp = tmpSelf.pict.PictApplication;
				if (tmpApp && typeof tmpApp.showNotes === 'function') { tmpApp.showNotes(); return; }
			}
			tmpSelf.show();
		}).catch(() => {});
	}
}

module.exports = PictViewNotesPage;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-NotesPage',
	DefaultRenderable: 'Plansheet-NotesPage',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.ps-notespage.ps-page { color: var(--theme-color-text-primary, #1d2230); }
		.ps-notespage-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; }
		.ps-notespage-head h2 { font-size: 22px; font-weight: 600; margin: 0; color: var(--theme-color-text-primary, #1f2430); }
		.ps-notespage-body { display: flex; gap: 20px; align-items: flex-start; }
		.ps-notespage-list-col { flex: 0 0 280px; max-width: 280px; display: flex; flex-direction: column; gap: 8px; }
		.ps-notespage-editor-col { flex: 1 1 auto; min-width: 0; }
		.ps-notespage-new { width: 100%; padding: 8px 10px; font-size: 13px; cursor: pointer;
			border: 1px dashed var(--theme-color-border-default, #c9d0db); border-radius: 8px;
			background: transparent; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-notespage-new:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); color: var(--theme-color-text-primary, #2c3140); }
		.ps-notespage-notelist { display: flex; flex-direction: column; gap: 2px; }
		.ps-notespage-noterow { text-align: left; padding: 8px 10px; font-size: 14px; border: none; border-radius: 7px;
			background: transparent; color: var(--theme-color-text-primary, #2c3140); cursor: pointer; width: 100%;
			white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.ps-notespage-noterow:hover { background: var(--theme-color-background-hover, #eef1f6); }
		.ps-notespage-noterow.is-active { background: var(--theme-color-background-hover, #e7edf8); font-weight: 600; }
		.ps-notespage-empty { color: var(--theme-color-text-secondary, #8a93a6); font-style: italic; font-size: 13px; margin: 6px 2px; }
		.ps-notespage-editor { display: flex; flex-direction: column; gap: 8px; }
		.ps-notespage-noteTitle { padding: 8px 10px; font-size: 15px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 7px; }
		.ps-notespage-mde { border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 7px; overflow: hidden;
			background: var(--theme-color-background-panel, #fff); }
		.ps-notespage-mde .cm-editor { min-height: 360px; }
		.ps-notespage-mde .cm-content { min-height: 360px; }
		.ps-notespage-editor-actions { display: flex; gap: 8px; }
		.ps-notespage-save { flex: 0 0 auto; padding: 8px 16px; font-size: 14px; cursor: pointer; border: none; border-radius: 7px;
			background: var(--theme-color-brand-primary, #3b6fd4); color: var(--theme-color-text-on-brand, #fff); }
		.ps-notespage-del { padding: 8px 14px; font-size: 14px; cursor: pointer; border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 7px; background: transparent; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-notespage-hint { color: var(--theme-color-text-secondary, #8a93a6); font-style: italic; font-size: 14px; padding: 40px 4px; text-align: center; }
		/* On narrow / phone width, stack the columns and let the editor use the full width. */
		@media (max-width: 860px)
		{
			.ps-notespage-body { flex-direction: column; }
			.ps-notespage-list-col { flex: 1 1 auto; max-width: none; width: 100%; }
			.ps-notespage-editor-col { width: 100%; }
			.ps-notespage-mde, .ps-notespage-editor { width: 100%; }
		}
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-NotesPage',
			Template: /*html*/`
<div class="ps-page ps-notespage">
	<div class="ps-notespage-head">
		<h2>Notes</h2>
	</div>
	<div class="ps-notespage-body">
		<div class="ps-notespage-list-col">
			<button class="ps-notespage-new" onclick="_Pict.views['Plansheet-NotesPage'].newNote()">+ New note</button>
			{~TS:NotesPage-Empty:AppData.NotesPage.NotesEmpty~}
			<div class="ps-notespage-notelist">{~TS:NotesPage-NoteRow:AppData.NotesPage.NoteRows~}</div>
		</div>
		<div class="ps-notespage-editor-col">
			{~TS:NotesPage-Editor:AppData.NotesPage.EditorSlot~}
			{~TS:NotesPage-NoSelection:AppData.NotesPage.NoSelectionSlot~}
		</div>
	</div>
</div>`
		},
		{
			Hash: 'NotesPage-Empty',
			Template: /*html*/`<p class="ps-notespage-empty">No notes yet. Jot down anything you want to keep.</p>`
		},
		{
			Hash: 'NotesPage-NoteRow',
			Template: /*html*/`<button class="ps-notespage-noterow{~NE:Record.Active^ is-active~}" onclick="_Pict.views['Plansheet-NotesPage'].selectNote({~D:Record.IDNote~})">{~D:Record.Title~}</button>`
		},
		{
			Hash: 'NotesPage-Editor',
			Template: /*html*/`
<div class="ps-notespage-editor">
	<input class="ps-notespage-noteTitle" value="{~D:Record.Title~}" placeholder="Title" oninput="_Pict.views['Plansheet-NotesPage'].onDraftTitle(this.value)" />
	<div class="ps-notespage-mde" id="PS-NotesPage-MDE"></div>
	<div class="ps-notespage-editor-actions">
		<button class="ps-notespage-save" onclick="_Pict.views['Plansheet-NotesPage'].saveNote()">Save</button>
		<button class="ps-notespage-del" onclick="_Pict.views['Plansheet-NotesPage'].deleteNote({~D:Record.IDNote~})">Delete</button>
	</div>
</div>`
		},
		{
			Hash: 'NotesPage-NoSelection',
			Template: /*html*/`<div class="ps-notespage-hint">Select a note on the left, or start a new one.</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'Plansheet-NotesPage',
			TemplateHash: 'Plansheet-NotesPage',
			ContentDestinationAddress: '#Plansheet-Workspace',
			RenderMethod: 'replace'
		}
	]
};
